

































































































































import {
	computed,
	defineComponent,
	nextTick,
	PropType,
	Ref,
	ref,
	watch,
} from '@vue/composition-api';
import { v } from '@front-app/components/form/validation/useValidation';
import { required } from '@front-app/components/form/validation/validators';
import { useUsername } from '@front-app/hooks/useTranslate';
import { useAsyncStatus } from '@front-app/hooks/useAsyncStatus';
import { CurrentUserStore, UserAvatarStore } from '@front-app/store/singletons/User';
import { FullUser, UserSalutation } from '@front-app/services/UserApi/UserModel';
import { useFocusFirstInvalidField } from '@front-app/hooks/useFocusFirstInvalidField';
import { UserProfileOptionsStore } from '@front-app/store/singletons/UserProfileOptions';
import { Size } from '@front-app/components/constants';
import { ButtonPalettes } from '@front-app/components/buttons/palettes';
import FormSection from '@front-app/components/form/FormSection.vue';
import FormLabel from '@front-app/components/form/FormLabel.vue';
import FormError from '@front-app/components/form/FormError.vue';
import FormInput from '@front-app/components/form/FormInput.vue';
import SalutationPicker from '@front-app/components/inputs/SalutationPicker.vue';
import CategoriesInput from '@front-app/components/inputs/CategoriesInput.vue';
import UserAvatar from '@front-app/components/user/avatar.vue';
import EditorWrapper from '../../layouts/form/EditorWrapper.vue';
import FileInput from '../../inputs/FileInput.vue';

export default defineComponent({
	components: {
		UserAvatar,
		EditorWrapper,
		FormSection,
		FormLabel,
		FormError,
		FormInput,
		SalutationPicker,
		CategoriesInput,
		FileInput,
	},
	props: {
		user: {
			type: Object as PropType<FullUser>,
			required: true,
		},
	},
	setup(props, ctx) {
		const { update } = CurrentUserStore.actions;

		const initialSalutation = computed(() => props.user.salutation);
		const salutation = ref(UserSalutation.NONE);
		watch(initialSalutation, (curr) => {
			salutation.value = curr;
		}, { immediate: true });

		const initialFirstName = computed(() => props.user.firstName);
		const firstName = ref('');
		watch(initialFirstName, (curr) => {
			firstName.value = curr;
		}, { immediate: true });

		const initialLastName = computed(() => props.user.lastName);
		const lastName = ref('');
		watch(initialLastName, (curr) => {
			lastName.value = curr;
		}, { immediate: true });

		const initialMobilePhone = computed(() => props.user.mobile);
		const mobilePhone = ref('');
		watch(initialMobilePhone, (curr) => {
			mobilePhone.value = curr;
		}, { immediate: true });

		const actualCredentials = computed(() => props.user.credentials);
		const credentials: Ref<string[]> = ref([]);
		const newCredentials: Ref<string[]> = ref([]);
		watch(actualCredentials, (curr) => {
			credentials.value = [...curr] || [];
		}, { immediate: true });
		const addCredential = (newCredential: string) => {
			credentials.value = [...credentials.value, newCredential];
		};
		const removeCredential = (oldCredential: string) => {
			credentials.value = credentials.value.filter((val) => val !== oldCredential);
		};
		const addNewCredential = (newCredential: string) => {
			newCredentials.value = [...newCredentials.value, newCredential];
		};
		const removeNewCredential = (oldCredential: string) => {
			newCredentials.value = newCredentials.value.filter((val) => val !== oldCredential);
		};

		const allCredentials = computed(() => UserProfileOptionsStore.credentials.value.map(({ name }) => name));

		const validation = v({
			firstName: {
				value: firstName,
				rules: [required()],
			},
			lastName: {
				value: lastName,
				rules: [required()],
			},
		});

		const {
			focusFirstInvalid,
		} = useFocusFirstInvalidField({ containerSelector: '.user-profile__update-form' });

		const {
			track,
			clear,
			isLoading,
			isError,
			isSuccess,
		} = useAsyncStatus();

		const saveUser = async () => {
			validation.validate();
			if (!validation.isValid.value) {
				await nextTick();
				focusFirstInvalid();
				return;
			}
			await track(update({
				salutation: salutation.value,
				firstName: firstName.value,
				lastName: lastName.value,
				credentials: {
					credentials: ([] as string[]).concat(credentials.value, newCredentials.value),
				},
				mobile: mobilePhone.value,
			}));
			clear(4000);
		};

		// Avatar stuff
		const imageAvatarChangeRequest = async (newFiles: File[]) => {
			if (newFiles.length < 1) {
				return;
			}
			const avatarRequest = newFiles[0];
			ctx.emit('change-avatar', avatarRequest);
		};

		return {
			// State
			salutation,
			firstName,
			lastName,
			allCredentials,
			credentials,
			newCredentials,
			addCredential,
			removeCredential,
			addNewCredential,
			removeNewCredential,
			mobilePhone,
			// Validation
			validation,
			// Saving
			saveUser,
			isLoading,
			isError,
			isSuccess,
			// Avatar
			getUsername: useUsername(ctx),
			getAvatar: UserAvatarStore.get,
			fetchAvatar: UserAvatarStore.fetch,
			imageAvatarChangeRequest,
			// Util
			Size,
			ButtonPalettes,
		};
	},
});
